import { Component, Input, OnInit, OnDestroy } from "@angular/core";

import { Subscription } from "rxjs";
import {
  OtaUpdateService,
  OtaUpdateInfo,
} from "../../services/ota-update.service";
import { App } from "@capacitor/app";
import { Network } from "@capacitor/network";
import { Capacitor } from "@capacitor/core";

type UpdateState = "idle" | "downloading" | "error";

@Component({
  selector: "app-ota-update-modal",
  imports: [],
  templateUrl: "./ota-update-modal.component.html",
  styleUrls: ["./ota-update-modal.component.scss"],
})
export class OtaUpdateModalComponent implements OnInit, OnDestroy {
  /** Güncelleme bilgisi — AppComponent tarafından set edilir */
  @Input() updateInfo!: OtaUpdateInfo;

  state: UpdateState = "idle";
  progress = 0;
  errorMessage = "";
  errorDetail = ""; // Teknik hata detayı (kopyalanabilir)
  copied = false; // Kopyala butonu feedback

  private progressSub: Subscription | null = null;

  constructor(private otaService: OtaUpdateService) {}

  ngOnInit(): void {
    // Download progress'i dinle
    this.progressSub = this.otaService.downloadProgress.subscribe((pct) => {
      this.progress = pct;
    });
  }

  ngOnDestroy(): void {
    this.progressSub?.unsubscribe();
  }

  /** Kullanıcı "Güncelle" butonuna bastığında çağrılır */
  async startUpdate(): Promise<void> {
    if (this.state === "downloading") return;

    if (this.updateInfo.isStoreUpdate) {
      // Mağaza güncellemesi: URL'yi (Google Play/App Store) sistem tarayıcısında veya market uygulamasında aç.
      window.open(this.updateInfo.url, "_system");
      return;
    }

    this.state = "downloading";
    this.progress = 0;
    this.errorMessage = "";

    try {
      await this.otaService.downloadAndApply(this.updateInfo);
      // downloadAndApply başarılı → uygulama reload olur, buraya ulaşılmaz
    } catch (err: any) {
      console.error("[OTA Modal] Update failed:", err);
      this.state = "error";
      this.errorMessage =
        "Güncelleme başarısız oldu. Lütfen internet bağlantınızı kontrol edip tekrar deneyin.";

      // Güvenli hata detayı — async, best-effort
      try {
        this.errorDetail = await this.buildErrorDetail(err);
      } catch {
        this.errorDetail = "UNKNOWN_ERROR";
      }
    }
  }

  /** Hata sonrası tekrar deneme */
  retry(): void {
    this.state = "idle";
    this.errorMessage = "";
    this.errorDetail = "";
    this.progress = 0;
    this.copied = false;
  }

  /** Hata detayını panoya kopyalar */
  async copyError(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.errorDetail);
      this.copied = true;
      setTimeout(() => (this.copied = false), 2500);
    } catch {
      // Fallback: execCommand (eski Android WebView)
      const el = document.createElement("textarea");
      el.value = this.errorDetail;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      this.copied = true;
      setTimeout(() => (this.copied = false), 2500);
    }
  }

  /**
   * Hata nesnesini güvenli, anlamlı bir etikete dönüştürür.
   * err.message içeride okunur ama çıktıya yansıtılmaz — URL sızmaz.
   */
  private classifyOtaError(err: any): string {
    if (!err) return "UNKNOWN_ERROR";

    const status = err?.status;

    // Native bridge error dönütleri
    const rawMsg =
      typeof err === "string" ? err : err?.message || err?.errorMessage || "";
    const msg = rawMsg.toLowerCase();
    const name = (typeof err === "object" ? err?.name ?? "" : "").toLowerCase();
    const code = (typeof err === "object" ? err?.code ?? "" : "").toLowerCase();

    // HTTP durum koduna göre
    if (status === 401 || status === 403) return "ZIP_LINK_EXPIRED_OR_DENIED";
    if (status === 404) return "ZIP_NOT_FOUND_ON_SERVER";
    if (status === 500 || status === 502 || status === 503)
      return "SERVER_ERROR";
    if (status >= 400 && status < 600) return `HTTP_ERROR_${status}`;

    // Format / JSON Parse hataları
    if (
      name === "syntaxerror" ||
      msg.includes("unexpected token") ||
      msg.includes("valid json") ||
      msg.includes("json parse")
    )
      return "INVALID_RESPONSE_FORMAT";

    // Capgo ve genel hata anahtar kelimeleri
    const match = (keyword: string) =>
      msg.includes(keyword) || code.includes(keyword);

    if (match("checksum") || match("hash")) return "CHECKSUM_MISMATCH";
    if (match("storage") || match("disk") || match("enospace"))
      return "STORAGE_FULL";
    if (match("invalid bundle") || match("invalid zip") || match("corrupt"))
      return "BUNDLE_CORRUPT";
    if (match("unzip") || match("extract")) return "EXTRACTION_FAILED";
    if (match("eacces") || match("permission denied"))
      return "PERMISSION_DENIED";
    if (match("enoent") || match("no such file")) return "FILE_SYSTEM_ERROR";
    if (
      match("install") ||
      match("set failed") ||
      match("could not set") ||
      match("update failed") ||
      match("reset failed")
    )
      return "INSTALL_FAILED";
    if (match("timeout")) return "TIMEOUT";
    if (name === "aborterror" || match("abort")) return "DOWNLOAD_ABORTED";

    // DNS ve Sunucu Çökme Hataları
    if (match("enotfound") || match("econnrefused"))
      return "SERVER_UNREACHABLE";

    // Native Ağ Hataları
    if (match("offline") || match("internet connection"))
      return "NETWORK_OFFLINE";

    // İndirme URL'sine Ulaşılamaması
    if (match("download")) return "DOWNLOAD_URL_UNREACHABLE";

    // Genel Ağ Hataları
    if (
      name === "networkerror" ||
      name === "typeerror" ||
      match("network") ||
      match("fetch") ||
      match("failed to fetch") ||
      match("econnreset")
    )
      return "NETWORK_ERROR";

    if (match("cors") || match("access-control")) return "CORS_ERROR";

    // err nesnesi string ise kendisini , değilse code/name bilgisini dön
    if (typeof err === "string")
      return (
        err
          .substring(0, 30)
          .replace(/[^a-zA-Z0-9_ -]/g, "")
          .trim()
          .toUpperCase()
          .replace(/\s+/g, "_") || "UNKNOWN_ERROR"
      );
    return err?.code || err?.name || "UNKNOWN_ERROR";
  }

  /**
   * Örnek çıktı:
   *   v2.11.2-rc3 | NETWORK_ERROR | phase:download | build:42
   *   | os:android/13 | net:wifi | http:0 | pct:34
   *   | storage:1.2GB | t:1751910812 | did:a1b2c3d4
   */
  private async buildErrorDetail(err: any): Promise<string> {
    const parts: string[] = [];

    // ── 1. Versiyon & Build ──────────────────────────────────────────────────
    parts.push(`v${this.updateInfo?.version ?? "?"}`);
    try {
      const info = await App.getInfo();
      parts.push(`build:${info.build}`);
    } catch {}

    // ── 2. Hata Kodu & Aşama ────────────────────────────────────────────────
    parts.push(this.classifyOtaError(err));
    if (err?._phase) parts.push(`phase:${err._phase}`);
    if (err?.status) parts.push(`http:${err.status}`);

    // ── 3. İşletim Sistemi ─
    try {
      const ua = navigator.userAgent;
      // Android: "Android 13" biçimi
      const androidMatch = ua.match(/Android\s([\d.]+)/);
      if (androidMatch) {
        parts.push(`os:android/${androidMatch[1]}`);
      } else {
        parts.push("os:android");
      }
    } catch {}

    // ── 4. Ağ Bağlantı Tipi ────────────
    try {
      const netStatus = await Network.getStatus();
      if (!netStatus.connected) {
        parts.push("net:none");
      } else {
        parts.push(`net:${netStatus.connectionType}`);
      }
    } catch {
      try {
        parts.push(`net:${navigator.onLine ? "on" : "off"}`);
      } catch {}
    }

    // ── 5. İndirme İlerlemesi ────────────────────────────────────────────────
    if (this.progress > 0) parts.push(`pct:${this.progress}`);

    // ── 6. Boş Depolama Alanı ──
    try {
      if (navigator.storage && (navigator.storage as any).estimate) {
        const est = (await (navigator.storage as any).estimate()) as {
          quota?: number;
          usage?: number;
        };
        if (est.quota != null && est.usage != null) {
          const freeBytes = est.quota - est.usage;
          const freeGB = (freeBytes / 1024 ** 3).toFixed(1);
          parts.push(`storage:${freeGB}GB`);
        }
      }
    } catch {}

    // ── 7. Tarih ve Saat ─────────────────────────────────────────────────────
    const now = new Date();
    const dateStr =
      now.getFullYear() +
      "-" +
      String(now.getMonth() + 1).padStart(2, "0") +
      "-" +
      String(now.getDate()).padStart(2, "0") +
      " " +
      String(now.getHours()).padStart(2, "0") +
      ":" +
      String(now.getMinutes()).padStart(2, "0");
    parts.push(`t:${dateStr}`);

    // ── 8. Anonim Cihaz ID ───
    try {
      const DID_KEY = "_ota_did";
      let did = localStorage.getItem(DID_KEY);
      if (!did) {
        // 8 karakterlik hex ID üret
        did = Math.random().toString(16).slice(2, 10);
        localStorage.setItem(DID_KEY, did);
      }
      parts.push(`did:${did}`);
    } catch {}

    return parts.join(" | ");
  }
}
